/**
 * components/PlaybookAccordion.tsx
 * Accordion display for playbooks
 *
 * Shows list of playbooks in accordion format with summary when collapsed,
 * details when expanded. Follows FeeHierarchyTree accordion pattern.
 */

// 1. External dependencies
import React from 'react';
import { Plus, Minus, Edit, Trash2, BookOpen, Tag, MoreVertical, AlertTriangle, Target } from 'lucide-react';

// 2. Internal absolute imports
import {
  Accordion,
  AccordionItem,
  Button,
  Chip,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem
} from '@enfomentis/ui';

// 3. Relative imports
import type { PlaybookDraft, CriteriaItemDraft, MarketConditionCategoryDraft, ConfluenceDraft, EntryModelDraft } from '../hooks/usePlaybookBuilder';

/**
 * Props for PlaybookAccordion component
 */
interface PlaybookAccordionProps {
  /** List of playbooks to display */
  playbooks: PlaybookDraft[];
  /** Available criteria items */
  criteriaItems: CriteriaItemDraft[];
  /** Market condition categories */
  marketConditionCategories: MarketConditionCategoryDraft[];
  /** Available confluences */
  confluences: ConfluenceDraft[];
  /** Available entry models */
  entryModels: EntryModelDraft[];
  /** Handler for editing a playbook */
  onEdit: (tempId: string) => void;
  /** Handler for deleting a playbook */
  onDelete: (tempId: string) => void;
  /** Handler for adding criteria to a playbook */
  onAddCriteria: (tempId: string) => void;
  /** Handler for adding confluences to a playbook */
  onAddConfluences: (tempId: string) => void;
  /** Handler for adding entry models to a playbook */
  onAddEntryModels: (tempId: string) => void;
  /** Set of playbook IDs to highlight (for validation feedback) */
  highlightedPlaybookIds?: Set<string>;
}

/**
 * Empty state component
 */
const EmptyState: React.FC = () => (
  <div className="flex-1 flex items-center justify-center py-12">
    <div className="text-center max-w-md">
      <BookOpen className="w-16 h-16 text-text-secondary mx-auto mb-4" />
      <h4 className="text-lg font-medium text-text-primary mb-2">No Playbooks Yet</h4>
      <p className="text-sm text-text-secondary">
        Start building your trading strategy by creating your first playbook.
        Click "Add Playbook" above to get started.
      </p>
    </div>
  </div>
);

/**
 * Playbook accordion component
 */
export const PlaybookAccordion: React.FC<PlaybookAccordionProps> = ({
  playbooks,
  criteriaItems,
  marketConditionCategories,
  confluences,
  entryModels,
  onEdit,
  onDelete,
  onAddCriteria,
  onAddConfluences,
  onAddEntryModels,
  highlightedPlaybookIds
}) => {
  // Default expand the most recently created playbook (last in array)
  const defaultExpandedKeys = React.useMemo(() => {
    if (playbooks.length > 0) {
      const lastPlaybook = playbooks[playbooks.length - 1];
      if (lastPlaybook) {
        return new Set([lastPlaybook.tempId]);
      }
    }
    return new Set<string>();
  }, [playbooks]); // Depend on playbooks to ensure correct state
  /**
   * Get criteria item by ID
   */
  const getCriteriaById = (criteriaId: string): CriteriaItemDraft | undefined => {
    return criteriaItems.find((c) => c.tempId === criteriaId);
  };

  /**
   * Get category by ID
   */
  const getCategoryById = (categoryId: string): MarketConditionCategoryDraft | undefined => {
    return marketConditionCategories.find((cat) => cat.tempId === categoryId);
  };

  /**
   * Get category for a criteria item
   */
  const getCategoryForCriteria = (criteriaId: string): MarketConditionCategoryDraft | undefined => {
    const item = getCriteriaById(criteriaId);
    if (!item?.categoryId) return undefined;
    return getCategoryById(item.categoryId);
  };

  /**
   * Get confluence by ID
   */
  const getConfluenceById = (confluenceId: string): ConfluenceDraft | undefined => {
    return confluences.find((c) => c.tempId === confluenceId);
  };

  /**
   * Get entry model by ID
   */
  const getEntryModelById = (modelId: string): EntryModelDraft | undefined => {
    return entryModels.find((m) => m.tempId === modelId);
  };

  // Show empty state if no playbooks
  if (playbooks.length === 0) {
    return <EmptyState />;
  }

  return (
    <Accordion
      variant="bordered"
      selectionMode="multiple"
      className="w-full"
      defaultSelectedKeys={defaultExpandedKeys}
    >
      {playbooks.map((playbook) => {
        // Count components
        const criteriaCount = playbook.criteriaIds?.length || 0;
        const confluenceCount = playbook.confluences?.length || 0;
        const entryModelCount = playbook.entryModelIds?.length || 0;

        const summaryText = `${criteriaCount} criteria • ${confluenceCount} confluences • ${entryModelCount} entry models`;

        return (
          <AccordionItem
            key={playbook.tempId}
            textValue={playbook.name}
            indicator={({ isOpen }) =>
              isOpen ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />
            }
            title={
              <div className="flex items-center justify-between w-full pr-2">
                <div className="flex-1">
                  <h4 className="font-medium text-base text-text-primary">{playbook.name}</h4>
                  <p className="hidden sm:block text-sm text-text-secondary mt-0.5">{summaryText}</p>
                </div>
                {/* Dropdown menu for Edit/Delete (both mobile and desktop) */}
                <div onClick={(e) => e.stopPropagation()}>
                  <Dropdown>
                    <DropdownTrigger>
                      <div className={
                        highlightedPlaybookIds?.has(playbook.tempId)
                          ? "p-2 rounded-md bg-warning-100 cursor-pointer transition-colors sm:bg-transparent sm:hover:bg-default-100"
                          : "p-2 rounded-md hover:bg-default-100 cursor-pointer transition-colors"
                      }>
                        <MoreVertical className={
                          highlightedPlaybookIds?.has(playbook.tempId)
                            ? "w-4 h-4 text-warning-500 sm:text-default-500"
                            : "w-4 h-4 text-default-500"
                        } />
                      </div>
                    </DropdownTrigger>
                    <DropdownMenu
                      aria-label="Playbook actions"
                      onAction={(key) => {
                        if (key === 'edit') {
                          onEdit(playbook.tempId);
                        } else if (key === 'criteria') {
                          onAddCriteria(playbook.tempId);
                        } else if (key === 'confluences') {
                          onAddConfluences(playbook.tempId);
                        } else if (key === 'entryModels') {
                          onAddEntryModels(playbook.tempId);
                        } else if (key === 'delete') {
                          onDelete(playbook.tempId);
                        }
                      }}
                    >
                      <DropdownItem key="edit" startContent={<Edit className="w-4 h-4 text-text-primary" />} >
                        Edit Playbook
                      </DropdownItem>
                      {/* Add actions - Mobile only (Desktop uses footer buttons) */}
                      <DropdownItem key="criteria" startContent={<Tag className="w-4 h-4 text-secondary" />} className="sm:hidden text-secondary">
                        Add Criteria
                      </DropdownItem>
                      <DropdownItem key="confluences" startContent={<AlertTriangle className="w-4 h-4 text-warning" />} className="sm:hidden text-warning">
                        Add Confluences
                      </DropdownItem>
                      <DropdownItem key="entryModels" startContent={<Target className="w-4 h-4 text-success" />} className="sm:hidden text-success" classNames={{title:"font-semibold"}}>
                        Add Entry Models
                      </DropdownItem>
                      <DropdownItem
                        key="delete"
                        color="danger"
                        startContent={<Trash2 className="w-4 h-4 text-danger" />}
                        className="text-danger"
                      >
                        Delete
                      </DropdownItem>
                    </DropdownMenu>
                  </Dropdown>
                </div>
              </div>
            }
            classNames={{
              indicator: "order-first mr-2"
            }}
          >
            <div className="space-y-4 pb-3">
              {/* Description */}
              {playbook.description && (
                <div>
                  <h5 className="text-sm font-medium text-text-secondary mb-1">Description</h5>
                  <p className="text-sm text-text-primary">{playbook.description}</p>
                </div>
              )}

              {/* Philosophy */}
              {playbook.philosophy && (
                <div>
                  <h5 className="text-sm font-medium text-text-secondary mb-1">Philosophy</h5>
                  <p className="text-sm text-text-primary">{playbook.philosophy}</p>
                </div>
              )}

              {/* Criteria Items */}
              {criteriaCount > 0 && (
                <div>
                  <h5 className="text-sm font-medium text-text-secondary mb-2">Criteria Items</h5>
                  <div className="flex flex-wrap gap-2">
                    {playbook.criteriaIds?.map((criteriaId) => {
                      const item = getCriteriaById(criteriaId);
                      const category = getCategoryForCriteria(criteriaId);
                      if (!item) return null;

                      return (
                        <Chip
                          key={criteriaId}
                          size="sm"
                          variant="flat"
                          style={
                            category
                              ? {
                                  backgroundColor: `${category.color}20`,
                                  color: category.color
                                }
                              : undefined
                          }
                        >
                          {item.name}
                        </Chip>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Confluences */}
              {confluenceCount > 0 && (
                <div>
                  <h5 className="text-sm font-medium text-text-secondary mb-2">Confluences</h5>
                  <div className="flex flex-wrap gap-2">
                    {playbook.confluences?.map((link) => {
                      const confluence = getConfluenceById(link.confluenceId);
                      if (!confluence) return null;

                      return (
                        <Chip
                          key={link.confluenceId}
                          size="sm"
                          variant="flat"
                          color={link.polarity === 'positive' ? 'success' : 'warning'}
                        >
                          {confluence.name}
                        </Chip>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Entry Models */}
              {entryModelCount > 0 && (
                <div>
                  <h5 className="text-sm font-medium text-text-secondary mb-2">Entry Models</h5>
                  <div className="flex flex-wrap gap-2">
                    {playbook.entryModelIds?.map((modelId) => {
                      const model = getEntryModelById(modelId);
                      if (!model) return null;

                      return (
                        <Chip
                          key={modelId}
                          size="sm"
                          variant="flat"
                          color="primary"
                        >
                          {model.name}
                        </Chip>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Empty state */}
              {criteriaCount === 0 && confluenceCount === 0 && entryModelCount === 0 && (
                <div className="text-sm text-text-secondary italic">
                  <span className="hidden sm:inline">No criteria, confluences, or entry models added yet. Use the buttons below to add.</span>
                  <span className="sm:hidden">
                    No criteria, confluences, or entry models added yet. Use the menu button <MoreVertical className="w-4 h-4 text-default-500 inline-block align-text-center" /> above to add.
                  </span>
                </div>
              )}

              {/* Add action buttons - Desktop only (Mobile uses header menu) */}
              <div className="hidden sm:flex gap-2 pt-2 border-t border-default-200">
                <Button
                  size="sm"
                  variant="bordered"
                  color="secondary"
                  startContent={<Tag className="w-3 h-3" />}
                  onPress={() => onAddCriteria(playbook.tempId)}
                >
                  Add Criteria
                </Button>
                <Button
                  size="sm"
                  variant="bordered"
                  color="primary"
                  startContent={<AlertTriangle className="w-3 h-3" />}
                  onPress={() => onAddConfluences(playbook.tempId)}
                >
                  Add Confluences
                </Button>
                <Button
                  size="sm"
                  variant={highlightedPlaybookIds?.has(playbook.tempId) ? "solid" : "bordered"}
                  color={highlightedPlaybookIds?.has(playbook.tempId) ? "warning" : "success"}
                  startContent={<Target className="w-3 h-3" />}
                  onPress={() => onAddEntryModels(playbook.tempId)}
                >
                  Add Entry Models
                </Button>
              </div>
            </div>
          </AccordionItem>
        );
      })}
    </Accordion>
  );
};
